import React, { useEffect, useState } from "react";
import {
  RingProgress,
  Text,
  Group,
  Grid,
  Table,
  Loader,
  Center,
  Badge,
  Select,
  ScrollArea,
  Button,
} from "@mantine/core";
import { IconPrinter } from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { url } from "../../settings/linkUrl";
import chrome from "../AdminPageChrome.module.css";

interface dataProgress {
  label: string;
  stats: string;
  progress: number;
  color: string;
}

interface SetSizeRow {
  set_size: string;
  label: string;
  total: number;
}

interface SetSizeByProgrameRow {
  vut: string;
  levelk: string;
  programe: string;
  set_size: string;
  label: string;
  total: number;
}

interface SetSizeSummary {
  items: SetSizeRow[];
  grand_total: number;
  items_by_programe: SetSizeByProgrameRow[];
}

type GroupByKey = "vut" | "levelk" | "programe";

interface GroupSummaryRow {
  group_name: string;
  total: number;
  paid: number;
  pending: number;
  not_registered: number;
}

interface GroupSummary {
  group: GroupByKey;
  group_label: string;
  items: GroupSummaryRow[];
  totals: GroupSummaryRow;
}

const GROUP_OPTIONS = [
  { value: "vut", label: "แยกตามวุฒิ (ป.ตรี / ป.โท / ป.เอก)" },
  { value: "levelk", label: "แยกตามคณะ" },
  { value: "programe", label: "แยกตามสาขาวิชา / หลักสูตร" },
];

export default function AdministratorHomePage() {
  const [dataProgress, setDataProgress] = useState<dataProgress[]>([]);
  const [dataMoney, setDataMoney] = useState<any[]>([]);
  const [dataSetSize, setDataSetSize] = useState<SetSizeSummary>({
    items: [],
    grand_total: 0,
    items_by_programe: [],
  });
  const [loadingChart, setLoadingChart] = useState(true);
  const [loadingMoney, setLoadingMoney] = useState(true);
  const [loadingSetSize, setLoadingSetSize] = useState(true);
  const [groupBy, setGroupBy] = useState<GroupByKey>("vut");
  const [dataByGroup, setDataByGroup] = useState<GroupSummary | null>(null);
  const [loadingByGroup, setLoadingByGroup] = useState(true);
  const [errorByGroup, setErrorByGroup] = useState("");
  const [errorMoney, setErrorMoney] = useState("");
  const [errorSetSize, setErrorSetSize] = useState("");

  const authHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  const LoadPage = () => {
    setLoadingChart(true);
    fetch(`${url}listname/countchart`, {
      method: "GET",
      headers: authHeaders(),
    })
      .then((respone) => {
        if (!respone.ok) throw Error(respone.statusText);
        return respone.json();
      })
      .then((json) => {
        setDataProgress(Array.isArray(json) ? json : []);
      })
      .catch(() => {
        setDataProgress([]);
      })
      .finally(() => setLoadingChart(false));
  };

  const LoadMoney = () => {
    setLoadingMoney(true);
    setErrorMoney("");
    fetch(`${url}listname/countmoney`, {
      method: "GET",
      headers: authHeaders(),
    })
      .then((respone) => {
        if (!respone.ok) throw Error(respone.statusText);
        return respone.json();
      })
      .then((json) => {
        if (Array.isArray(json)) {
          setDataMoney(json);
        } else {
          setDataMoney([]);
          setErrorMoney(json?.message || "โหลดยอดเงินไม่สำเร็จ");
        }
      })
      .catch((error) => {
        setDataMoney([]);
        setErrorMoney(error.message || "โหลดยอดเงินไม่สำเร็จ");
      })
      .finally(() => setLoadingMoney(false));
  };

  const LoadSetSize = () => {
    setLoadingSetSize(true);
    setErrorSetSize("");
    fetch(`${url}listname/countsetsize`, {
      method: "GET",
      headers: authHeaders(),
    })
      .then((respone) => {
        if (!respone.ok) throw Error(respone.statusText);
        return respone.json();
      })
      .then((json) => {
        if (Array.isArray(json?.items)) {
          setDataSetSize({
            items: json.items,
            grand_total: Number(json.grand_total) || 0,
            items_by_programe: Array.isArray(json.items_by_programe)
              ? json.items_by_programe
              : [],
          });
        } else {
          setDataSetSize({ items: [], grand_total: 0, items_by_programe: [] });
          setErrorSetSize(json?.message || "โหลดสรุปไซส์ครุยไม่สำเร็จ");
        }
      })
      .catch((error) => {
        setDataSetSize({ items: [], grand_total: 0, items_by_programe: [] });
        setErrorSetSize(error.message || "โหลดสรุปไซส์ครุยไม่สำเร็จ");
      })
      .finally(() => setLoadingSetSize(false));
  };

  const LoadByGroup = (group: GroupByKey) => {
    setLoadingByGroup(true);
    setErrorByGroup("");
    fetch(`${url}listname/countbygroup?group=${group}`, {
      method: "GET",
      headers: authHeaders(),
    })
      .then((respone) => {
        if (!respone.ok) throw Error(respone.statusText);
        return respone.json();
      })
      .then((json) => {
        if (Array.isArray(json?.items)) {
          setDataByGroup({
            group: json.group,
            group_label: json.group_label,
            items: json.items,
            totals: json.totals,
          });
        } else {
          setDataByGroup(null);
          setErrorByGroup(json?.message || "โหลดสรุปตามกลุ่มไม่สำเร็จ");
        }
      })
      .catch((error) => {
        setDataByGroup(null);
        setErrorByGroup(error.message || "โหลดสรุปตามกลุ่มไม่สำเร็จ");
      })
      .finally(() => setLoadingByGroup(false));
  };

  useEffect(() => {
    LoadPage();
    LoadMoney();
    LoadSetSize();
  }, []);

  useEffect(() => {
    LoadByGroup(groupBy);
  }, [groupBy]);

  const paidStat = dataProgress.find((s) =>
    s.label.includes("ชำระเงินแล้ว")
  );

  const stats = dataProgress.map((stat) => {
    return (
      <Grid.Col span={{ base: 12, sm: 6, md: 4 }} key={stat.label}>
        <div className="admin-stat-panel">
          <Group wrap="nowrap" align="center">
            <RingProgress
              size={110}
              thickness={12}
              roundCaps
              sections={[{ value: Number(stat.progress) || 0, color: stat.color }]}
              label={
                <Text size="lg" ta="center" c={stat.color} fw={700}>
                  {stat.progress}%
                </Text>
              }
            />
            <div>
              <Text c="dimmed" size="xs" tt="uppercase" fw={600}>
                {stat.label}
              </Text>
              <Text fw={700} size="xl" c="var(--admin-navy)">
                {stat.stats}
              </Text>
            </div>
          </Group>
        </div>
      </Grid.Col>
    );
  });

  const moneyRows = dataMoney.map((element: any, index) => (
    <Table.Tr key={`${element.name_detail}-${index}`}>
      <Table.Td style={{ textAlign: "center" }}>{index + 1}</Table.Td>
      <Table.Td>{element.name_detail}</Table.Td>
      <Table.Td style={{ textAlign: "center" }}>{element.register}</Table.Td>
      <Table.Td style={{ textAlign: "right", fontWeight: 600 }}>
        {element.money}
      </Table.Td>
    </Table.Tr>
  ));

  const sizeRows = dataSetSize.items.map((row, index) => (
    <Table.Tr key={row.set_size}>
      <Table.Td style={{ textAlign: "center" }}>{index + 1}</Table.Td>
      <Table.Td>
        <Group gap="xs">
          <Badge color="blue" variant="light">
            {row.set_size}
          </Badge>
          <Text size="sm">{row.label}</Text>
        </Group>
      </Table.Td>
      <Table.Td style={{ textAlign: "center", fontWeight: 600 }}>
        {row.total.toLocaleString("th-TH")}
      </Table.Td>
    </Table.Tr>
  ));

  const sizeByProgrameRows = dataSetSize.items_by_programe.map((row, index) => (
    <Table.Tr key={`${row.programe}-${row.set_size}-${index}`}>
      <Table.Td style={{ textAlign: "center" }}>{index + 1}</Table.Td>
      <Table.Td>{row.vut || "—"}</Table.Td>
      <Table.Td>{row.levelk || "—"}</Table.Td>
      <Table.Td>{row.programe}</Table.Td>
      <Table.Td>
        <Badge color="blue" variant="light">{row.set_size}</Badge>
      </Table.Td>
      <Table.Td style={{ textAlign: "center", fontWeight: 600 }}>
        {row.total.toLocaleString("th-TH")}
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <>
      <header className={chrome.pageHeader}>
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <div>
            <h1 className={chrome.title}>ภาพรวมการลงทะเบียน</h1>
            <p className={chrome.subtitle}>
              สรุปจำนวนบัณฑิตที่ชำระเงินแล้ว ยอดเงิน และไซส์ครุยวิทยฐานะที่จอง
            </p>
          </div>
          <Button
            component={Link}
            to="/administrator/report"
            color="blue"
            variant="light"
            leftSection={<IconPrinter size={16} />}
            className="no-print"
          >
            พิมพ์รายงานเสนอผู้บริหาร
          </Button>
        </Group>
      </header>

      {paidStat ? (
        <div className="admin-panel" style={{ marginBottom: "1rem" }}>
          <Text fw={600} c="var(--admin-navy)" size="lg">
            บัณฑิตที่ลงทะเบียนและชำระเงินแล้ว: {paidStat.stats} คน
          </Text>
          <Text size="sm" c="dimmed" mt={4}>
            นับจากสถานะ creck = 1 ในระบบลงทะเบียนซ้อมรับปริญญาบัตร
          </Text>
        </div>
      ) : null}

      {loadingChart && dataProgress.length === 0 ? (
        <Center py="xl">
          <Loader color="blue" />
        </Center>
      ) : (
        <Grid>{stats}</Grid>
      )}

      <div className="admin-panel" style={{ marginTop: "1.1rem" }}>
        <Group justify="space-between" align="flex-end" mb="sm" wrap="wrap">
          <div>
            <Text fw={600} c="var(--admin-navy)">
              สรุปจำนวนบัณฑิตแยกตามกลุ่ม
            </Text>
            <Text size="sm" c="dimmed" mt={4}>
              เลือกมุมมอง: วุฒิ / คณะ / สาขาวิชา
            </Text>
          </div>
          <Select
            label="แยกข้อมูลตาม"
            data={GROUP_OPTIONS}
            value={groupBy}
            onChange={(value) => setGroupBy((value as GroupByKey) || "vut")}
            style={{ minWidth: 280 }}
            allowDeselect={false}
          />
        </Group>
        <ScrollArea>
          <div className="admin-table-wrap">
            <Table highlightOnHover withRowBorders>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ textAlign: "center", width: 56 }}>#</Table.Th>
                  <Table.Th>{dataByGroup?.group_label || "กลุ่ม"}</Table.Th>
                  <Table.Th style={{ textAlign: "center" }}>ทั้งหมด</Table.Th>
                  <Table.Th style={{ textAlign: "center" }}>ชำระแล้ว</Table.Th>
                  <Table.Th style={{ textAlign: "center" }}>รอชำระ</Table.Th>
                  <Table.Th style={{ textAlign: "center" }}>ยังไม่ลงทะเบียน</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {dataByGroup?.items.map((row, index) => (
                  <Table.Tr key={`${row.group_name}-${index}`}>
                    <Table.Td style={{ textAlign: "center" }}>{index + 1}</Table.Td>
                    <Table.Td>{row.group_name}</Table.Td>
                    <Table.Td style={{ textAlign: "center" }}>{row.total.toLocaleString("th-TH")}</Table.Td>
                    <Table.Td style={{ textAlign: "center" }}>
                      <Badge color="teal" variant="light">{row.paid.toLocaleString("th-TH")}</Badge>
                    </Table.Td>
                    <Table.Td style={{ textAlign: "center" }}>
                      <Badge color="orange" variant="light">{row.pending.toLocaleString("th-TH")}</Badge>
                    </Table.Td>
                    <Table.Td style={{ textAlign: "center" }}>
                      <Badge color="red" variant="light">{row.not_registered.toLocaleString("th-TH")}</Badge>
                    </Table.Td>
                  </Table.Tr>
                ))}
                {dataByGroup?.totals ? (
                  <Table.Tr style={{ fontWeight: 700, background: "var(--admin-mist, #e8f1fa)" }}>
                    <Table.Td style={{ textAlign: "center" }}>—</Table.Td>
                    <Table.Td>รวมทั้งหมด</Table.Td>
                    <Table.Td style={{ textAlign: "center" }}>{dataByGroup.totals.total.toLocaleString("th-TH")}</Table.Td>
                    <Table.Td style={{ textAlign: "center" }}>{dataByGroup.totals.paid.toLocaleString("th-TH")}</Table.Td>
                    <Table.Td style={{ textAlign: "center" }}>{dataByGroup.totals.pending.toLocaleString("th-TH")}</Table.Td>
                    <Table.Td style={{ textAlign: "center" }}>{dataByGroup.totals.not_registered.toLocaleString("th-TH")}</Table.Td>
                  </Table.Tr>
                ) : null}
                {!loadingByGroup && (!dataByGroup?.items || dataByGroup.items.length === 0) ? (
                  <Table.Tr>
                    <Table.Td colSpan={6}>
                      <div className="admin-empty">
                        {errorByGroup
                          ? `เกิดข้อผิดพลาด: ${errorByGroup}`
                          : "ยังไม่มีข้อมูลสรุปตามกลุ่ม"}
                      </div>
                    </Table.Td>
                  </Table.Tr>
                ) : null}
                {loadingByGroup && (!dataByGroup?.items || dataByGroup.items.length === 0) ? (
                  <Table.Tr>
                    <Table.Td colSpan={6}>
                      <Center py="md">
                        <Loader size="sm" color="blue" />
                      </Center>
                    </Table.Td>
                  </Table.Tr>
                ) : null}
              </Table.Tbody>
            </Table>
          </div>
        </ScrollArea>
      </div>

      <Grid gap="md" mt="md">
        <Grid.Col span={{ base: 12, lg: 12 }}>
          <div className="admin-panel">
            <Group justify="space-between" mb="sm">
              <Text fw={600} c="var(--admin-navy)">
                สรุปไซส์ครุยวิทยฐานะ
              </Text>
              {loadingSetSize ? (
                <Group gap="xs">
                  <Loader size="sm" color="blue" />
                  <Text size="sm" c="dimmed">
                    กำลังโหลด...
                  </Text>
                </Group>
              ) : (
                <Text size="sm" c="dimmed">
                  รวม {dataSetSize.grand_total.toLocaleString("th-TH")} คน
                </Text>
              )}
            </Group>
            <Text size="sm" c="dimmed" mb="sm">
              เฉพาะบัณฑิตที่ชำระเงินแล้ว และเลือกซื้อ/เช่าครุย
            </Text>

            <Text fw={600} size="sm" mb="xs" c="var(--admin-navy)">
              สรุปรวมตามไซส์
            </Text>
            <div className="admin-table-wrap" style={{ maxWidth: 480, marginBottom: "1rem" }}>
              <Table highlightOnHover withRowBorders>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ textAlign: "center", width: 56 }}>#</Table.Th>
                    <Table.Th>ไซส์ที่จอง</Table.Th>
                    <Table.Th style={{ textAlign: "center" }}>จำนวน (คน)</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {sizeRows.length > 0 ? (
                    sizeRows
                  ) : (
                    <Table.Tr>
                      <Table.Td colSpan={3}>
                        <div className="admin-empty">
                          {loadingSetSize
                            ? "กำลังโหลดข้อมูลไซส์ครุย..."
                            : errorSetSize
                              ? `เกิดข้อผิดพลาด: ${errorSetSize}`
                              : "ยังไม่มีข้อมูลไซส์ครุย"}
                        </div>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
            </div>

            <Text fw={600} size="sm" mb="xs" c="var(--admin-navy)">
              แยกตามหลักสูตร / สาขาวิชา
            </Text>
            <ScrollArea h={320} type="auto">
              <div className="admin-table-wrap">
                <Table highlightOnHover withRowBorders>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ textAlign: "center", width: 48 }}>#</Table.Th>
                      <Table.Th>หลักสูตร (วุฒิ)</Table.Th>
                      <Table.Th>คณะ</Table.Th>
                      <Table.Th>สาขาวิชา</Table.Th>
                      <Table.Th style={{ textAlign: "center" }}>ไซส์</Table.Th>
                      <Table.Th style={{ textAlign: "center" }}>จำนวน</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {sizeByProgrameRows.length > 0 ? (
                      sizeByProgrameRows
                    ) : (
                      <Table.Tr>
                        <Table.Td colSpan={6}>
                          <div className="admin-empty">
                            {loadingSetSize
                              ? "กำลังโหลดข้อมูล..."
                              : "ยังไม่มีข้อมูลแยกตามหลักสูตร"}
                          </div>
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </Table.Tbody>
                </Table>
              </div>
            </ScrollArea>
          </div>
        </Grid.Col>

        <Grid.Col span={{ base: 12, lg: 6 }}>
          <div className="admin-panel" style={{ height: "100%" }}>
            <Group justify="space-between" mb="sm">
              <Text fw={600} c="var(--admin-navy)">
                สรุปยอดเงินตามรายการ
              </Text>
              {loadingMoney ? (
                <Group gap="xs">
                  <Loader size="sm" color="blue" />
                  <Text size="sm" c="dimmed">
                    กำลังโหลด...
                  </Text>
                </Group>
              ) : null}
            </Group>
            <div className="admin-table-wrap">
              <Table highlightOnHover withRowBorders>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ textAlign: "center", width: 56 }}>#</Table.Th>
                    <Table.Th>รายการ</Table.Th>
                    <Table.Th style={{ textAlign: "center" }}>จำนวน</Table.Th>
                    <Table.Th style={{ textAlign: "right" }}>ยอดเงิน</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {moneyRows.length > 0 ? (
                    moneyRows
                  ) : (
                    <Table.Tr>
                      <Table.Td colSpan={4}>
                        <div className="admin-empty">
                          {loadingMoney
                            ? "กำลังดึงข้อมูลจากระบบการเงิน..."
                            : errorMoney
                              ? `เกิดข้อผิดพลาด: ${errorMoney}`
                              : "ยังไม่มีข้อมูลยอดเงิน"}
                        </div>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
            </div>
          </div>
        </Grid.Col>
      </Grid>
    </>
  );
}
